import { useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import ChevronLeftIcon from '@/assets/icons/main-bottom-navigation/chevron-left.svg?react';
import CircleHelpIcon from '@/assets/icons/main-bottom-navigation/circle-help.svg?react';
import ClockIcon from '@/assets/icons/main-bottom-navigation/clock.svg?react';
import FileIcon from '@/assets/icons/main-bottom-navigation/file.svg?react';
import HowToPlayIcon from '@/assets/icons/main-bottom-navigation/how-to-play.svg?react';
import LogoutIcon from '@/assets/icons/main-bottom-navigation/logout.svg?react';
import MouseIcon from '@/assets/icons/main-bottom-navigation/mouse.svg?react';
import ReferIcon from '@/assets/icons/main-bottom-navigation/refer.svg?react';
import SettingsIcon from '@/assets/icons/main-bottom-navigation/settings.svg?react';
import ShieldIcon from '@/assets/icons/main-bottom-navigation/shield.svg?react';
import SidebarWordmark from '@/assets/icons/main-bottom-navigation/hq-logo.png';
import SidebarLogo from '@/assets/images/logo/sidebar-logo.svg';
import {
	CONTACT_URL,
	MOBILE_BOTTOM_NAV_ICONS,
	PRIVACY_POLICY_URL,
	ROUTES,
	TERMS_AND_CONDITIONS_URL,
} from '@/constants';
import { useCurrentAccessTokenSelector } from '@/store/slices/auth/session.slice';
import type { NavigationItem, User } from '@/types/layout.types';
import { cn } from '@/utils';
import { UserRole } from '@sportspredict/shared';

interface WebSidebarProps {
	bottomNav: NavigationItem[];
	navigation: NavigationItem[];
	currentTimezone: string;
	currentPageName: string;
	user: User | null | undefined;
	signOut: () => void | Promise<void>;
	onAccountClick: () => void;
	onHowToPlayClick: () => void;
	isLoadingUser: boolean;
	isRegisteredUser: boolean;
	isExpanded: boolean;
	onExpandedChange: (isExpanded: boolean) => void;
}

interface SidebarItemProps {
	label: string;
	icon: ComponentType<{ className?: string }>;
	isExpanded: boolean;
	iconClassName?: string;
	isActive?: boolean;
	href?: string;
	isExternal?: boolean;
	onClick?: () => void;
	detail?: string;
	className?: string;
}

interface SidebarPopoverPosition {
	left: number;
	top: number;
	arrowLeft: number;
	placement: 'above' | 'below';
}

const SIDEBAR_ICON_CLASS_NAME = 'max-h-6 max-w-6 shrink-0';
const SIDEBAR_POPOVER_HEIGHT = 36;
const SIDEBAR_POPOVER_MIN_WIDTH = 112;
const SIDEBAR_POPOVER_TARGET_OFFSET = 40;
const SIDEBAR_POPOVER_VIEWPORT_GAP = 8;

function SidebarPopover({
	anchorRef,
	label,
	detail,
	isVisible,
}: {
	anchorRef: RefObject<HTMLDivElement | null>;
	label: string;
	detail?: string;
	isVisible: boolean;
}) {
	const popoverRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<SidebarPopoverPosition | null>(null);

	useEffect(() => {
		const anchor = anchorRef.current;
		if (!isVisible || !anchor) return;

		const updatePosition = () => {
			const anchorRect = anchor.getBoundingClientRect();
			const popoverWidth = popoverRef.current?.offsetWidth ?? SIDEBAR_POPOVER_MIN_WIDTH;
			const targetCenter = anchorRect.left + anchorRect.width / 2;
			const preferredLeft = targetCenter - SIDEBAR_POPOVER_TARGET_OFFSET;
			const left = Math.min(
				Math.max(preferredLeft, SIDEBAR_POPOVER_VIEWPORT_GAP),
				window.innerWidth - popoverWidth - SIDEBAR_POPOVER_VIEWPORT_GAP
			);
			const placement =
				anchorRect.bottom + SIDEBAR_POPOVER_HEIGHT + SIDEBAR_POPOVER_VIEWPORT_GAP >
				window.innerHeight
					? 'above'
					: 'below';
			const top =
				placement === 'below' ? anchorRect.bottom + 2 : anchorRect.top - SIDEBAR_POPOVER_HEIGHT - 2;
			const arrowLeft = Math.min(Math.max(targetCenter - left - 28, 4), popoverWidth - 60);

			setPosition({ left, top, arrowLeft, placement });
		};

		updatePosition();
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);

		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [anchorRef, isVisible]);

	if (!isVisible || typeof document === 'undefined') return null;

	return createPortal(
		<div
			aria-hidden={!position}
			className="pointer-events-none fixed z-[70] flex h-9 min-w-28 items-center justify-center whitespace-nowrap rounded-[38px] bg-white/90 px-[34px] text-sm font-medium leading-normal text-[#111834] opacity-0 shadow-[0_10px_50px_rgba(0,0,0,0.30)] backdrop-blur-[65px] transition-opacity duration-150"
			ref={popoverRef}
			role="tooltip"
			style={
				position
					? {
							left: position.left,
							top: position.top,
							opacity: 1,
						}
					: undefined
			}
		>
			<svg
				aria-hidden="true"
				className={cn(
					'absolute h-[13px] w-14 fill-white/90',
					position?.placement === 'below' ? '-top-[13px] rotate-180' : '-bottom-[13px]'
				)}
				style={{ left: position?.arrowLeft ?? 12 }}
				viewBox="0 0 56 13"
			>
				<path
					clipRule="evenodd"
					d="M0 .002C2.04.002 4.08-.007 6.12.012c1.953.018 4.139.043 6.04.645 2.052.65 3.411 1.787 4.779 3.124.984.963 2.918 3.015 3.86 4.006.77.813 2.279 2.419 3.103 3.196C24.939 11.961 26.22 13 28 13s3.061-1.039 4.098-2.016c.824-.776 2.332-2.383 3.103-3.196.941-.99 2.875-3.043 3.86-4.006 1.37-1.337 2.727-2.474 4.779-3.124 1.901-.6 4.087-.627 6.039-.645C51.92-.006 53.96.003 56 .003V0H0v.002Z"
					fillRule="evenodd"
				/>
			</svg>
			{detail ? `${label}: ${detail}` : label}
		</div>,
		document.body
	);
}

const getSidebarItemClassName = ({
	isExpanded,
	isActive,
	detail,
	className,
}: Pick<SidebarItemProps, 'isExpanded' | 'isActive' | 'detail' | 'className'>) =>
	cn(
		'group flex shrink-0 rounded-lg text-[#414D73] transition-[width,color,background-color] duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667FF] focus-visible:ring-offset-2',
		isExpanded ? 'w-[260px] gap-4 px-4' : 'w-14 justify-center px-0',
		detail !== undefined ? 'h-20 items-start pt-4' : 'h-14 items-center',
		isActive ? 'text-[#2D6BFF]' : 'hover:text-[#2667FF]',
		className
	);

function SidebarItem({
	label,
	icon: Icon,
	isExpanded,
	iconClassName = SIDEBAR_ICON_CLASS_NAME,
	isActive = false,
	href,
	isExternal = false,
	onClick,
	detail,
	className,
}: SidebarItemProps) {
	const anchorRef = useRef<HTMLDivElement>(null);
	const [isPopoverRequested, setIsPopoverRequested] = useState(false);
	const isPopoverVisible = !isExpanded && isPopoverRequested;
	const content = (
		<>
			<span
				className={cn(
					'grid size-6 shrink-0 place-items-center',
					isActive && 'drop-shadow-[0_8px_8px_rgba(45,107,255,0.64)]'
				)}
			>
				<Icon aria-hidden="true" className={iconClassName} />
			</span>
			<span
				aria-hidden={!isExpanded}
				className={cn(
					'min-w-0 overflow-hidden text-left transition-[width,opacity,transform] duration-200 ease-out',
					isExpanded ? 'w-[180px] translate-x-0 opacity-100' : 'w-0 -translate-x-2 opacity-0'
				)}
			>
				<span
					className={cn(
						'sidebar-item-label block whitespace-nowrap text-base font-medium leading-[22px] tracking-[-0.4px]',
						isActive ? 'text-[#09090A]' : 'text-[#6E7899] group-hover:text-[#2667FF]'
					)}
				>
					{label}
				</span>
				{detail !== undefined ? (
					<span className="mt-0.5 block whitespace-nowrap text-xs leading-[17px] tracking-[-0.4px] text-[#6E7899]">
						{detail}
					</span>
				) : null}
			</span>
		</>
	);
	const sharedProps = {
		className: getSidebarItemClassName({ isExpanded, isActive, detail, className }),
		'aria-label': label,
		'aria-current': isActive ? ('page' as const) : undefined,
	};
	let item: ReactNode;

	if (href && isExternal) {
		item = (
			<a {...sharedProps} href={href} rel="noopener noreferrer" target="_blank">
				{content}
			</a>
		);
	} else if (href) {
		item = (
			<Link {...sharedProps} prefetch="viewport" onClick={onClick} to={href}>
				{content}
			</Link>
		);
	} else if (onClick) {
		item = (
			<button {...sharedProps} onClick={onClick} type="button">
				{content}
			</button>
		);
	} else {
		item = <div {...sharedProps}>{content}</div>;
	}

	return (
		<div
			className="w-fit"
			onBlur={() => setIsPopoverRequested(false)}
			onFocus={() => setIsPopoverRequested(true)}
			onPointerEnter={(event) => {
				if (event.pointerType === 'mouse') setIsPopoverRequested(true);
			}}
			onPointerLeave={() => setIsPopoverRequested(false)}
			ref={anchorRef}
		>
			{item}
			<SidebarPopover
				anchorRef={anchorRef}
				detail={detail}
				isVisible={isPopoverVisible}
				label={label === 'My Profile' ? 'Profile' : label}
			/>
		</div>
	);
}

function SidebarList({ children, className }: { children: ReactNode; className?: string }) {
	return <ul className={cn('flex flex-col gap-6', className)}>{children}</ul>;
}

function SidebarLegalNavigation({
	isExpanded,
	currentTimezone,
	className,
}: {
	isExpanded: boolean;
	currentTimezone: string;
	className?: string;
}) {
	return (
		<nav aria-label="Legal and support" className={className}>
			<SidebarList>
				<li>
					<SidebarItem
						href={TERMS_AND_CONDITIONS_URL}
						icon={FileIcon}
						isExpanded={isExpanded}
						isExternal
						label="Terms of Service"
					/>
				</li>
				<li>
					<SidebarItem
						href={PRIVACY_POLICY_URL}
						icon={ShieldIcon}
						isExpanded={isExpanded}
						isExternal
						label="Privacy Policy"
					/>
				</li>
				<li>
					<SidebarItem
						href={CONTACT_URL}
						icon={CircleHelpIcon}
						isExpanded={isExpanded}
						isExternal
						label="Support"
					/>
				</li>
				<li>
					<SidebarItem
						className="hover:text-[#414D73]"
						detail={currentTimezone}
						icon={ClockIcon}
						isExpanded={isExpanded}
						label="Timezone"
					/>
				</li>
			</SidebarList>
		</nav>
	);
}

function isPrimaryNavigationActive(itemName: string, currentPageName: string) {
	const normalizedItemName = itemName.toLowerCase();

	if (normalizedItemName === 'home') {
		return currentPageName === 'Home' || currentPageName === 'Auth';
	}

	if (normalizedItemName === 'events') {
		return ['Events', 'EventDetails', 'LobbyDetails'].includes(currentPageName);
	}

	if (normalizedItemName === 'leaderboard') {
		return ['Leaderboard', 'GlobalLeaderboard'].includes(currentPageName);
	}

	if (normalizedItemName === 'profile') {
		return ['Profile', 'EditProfile'].includes(currentPageName);
	}

	return currentPageName.toLowerCase() === normalizedItemName;
}

export function WebSidebar({
	bottomNav,
	navigation,
	currentTimezone,
	currentPageName,
	user,
	signOut,
	onAccountClick,
	onHowToPlayClick,
	isLoadingUser,
	isRegisteredUser,
	isExpanded,
	onExpandedChange,
}: WebSidebarProps) {
	const accessToken = useCurrentAccessTokenSelector();
	const scrollAreaRef = useRef<HTMLDivElement>(null);
	const [hasReachedScrollEnd, setHasReachedScrollEnd] = useState(false);
	const secondaryOrder = ['refer', 'account', 'howtoplay', 'admin'];
	const secondaryNavigation = navigation
		.filter((item) => secondaryOrder.includes(item.name.toLowerCase()))
		.sort((first, second) => {
			return (
				secondaryOrder.indexOf(first.name.toLowerCase()) -
				secondaryOrder.indexOf(second.name.toLowerCase())
			);
		});

	useEffect(() => {
		const scrollArea = scrollAreaRef.current;
		if (!scrollArea) return;

		const updateScrollEnd = () => {
			const remainingScroll =
				scrollArea.scrollHeight - scrollArea.scrollTop - scrollArea.clientHeight;
			setHasReachedScrollEnd(remainingScroll <= 8);
		};

		updateScrollEnd();
		const resizeObserver = new ResizeObserver(updateScrollEnd);
		resizeObserver.observe(scrollArea);

		return () => resizeObserver.disconnect();
	}, [isExpanded, isRegisteredUser, secondaryNavigation.length]);

	return (
		<aside
			aria-label="Sidebar navigation"
			className="group/sidebar fixed inset-y-4 left-4 z-50 overflow-hidden rounded-3xl border-[0.5px] border-[#E4E8F3] bg-white shadow-[0_16px_32px_-4px_rgba(93,104,133,0.24)] transition-[width] duration-300 ease-out"
			data-state={isExpanded ? 'expanded' : 'collapsed'}
			style={{ width: isExpanded ? 308 : 104 }}
		>
			<button
				aria-expanded={isExpanded}
				aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
				className="absolute left-[33px] top-[105px] z-20 grid size-[38px] place-items-center rounded-full border-[0.5px] border-[#E4E8F3] bg-white text-[#414D73] shadow-[0_1px_4px_rgba(12,12,13,0.05)] transition-colors hover:text-[#2667FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667FF]"
				onClick={() => onExpandedChange(!isExpanded)}
				type="button"
			>
				<ChevronLeftIcon
					aria-hidden="true"
					className={cn('transition-transform duration-300', !isExpanded && 'rotate-180')}
				/>
			</button>

			<div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
				<div
					className={cn(
						'flex min-h-0 flex-1 shrink flex-col gap-2.5 overflow-y-auto overscroll-contain transition-[width] duration-300 ease-out [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
						isExpanded ? 'w-[260px]' : 'w-14'
					)}
					onScroll={(event) => {
						const scrollArea = event.currentTarget;
						const remainingScroll =
							scrollArea.scrollHeight - scrollArea.scrollTop - scrollArea.clientHeight;
						setHasReachedScrollEnd(remainingScroll <= 8);
					}}
					ref={scrollAreaRef}
				>
					<div className="shrink-0">
						<Link
							aria-label="SportsPredict home"
							prefetch="viewport"
							className={cn(
								'flex h-14 items-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667FF] focus-visible:ring-offset-2',
								isExpanded
									? 'w-[260px] items-end justify-center pb-1'
									: 'w-14 justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_38%,#21478F_0%,#162E60_50%,#112148_75%,#0B1430_100%)] shadow-[0_8px_24px_rgba(38,103,255,0.32)]'
							)}
							to={ROUTES.HOME}
						>
							<img
								alt="SportsPredict.com"
								className={cn(
									'object-contain transition-[width,height,opacity] duration-200',
									isExpanded ? 'h-5 w-44 opacity-100' : 'h-6 w-[31px] opacity-100'
								)}
								src={isExpanded ? SidebarWordmark : SidebarLogo}
							/>
						</Link>

						<nav aria-label="Main" className="mt-11 border-t-[0.5px] border-[#E4E8F3] pb-4 pt-10">
							<SidebarList>
								{bottomNav.map((item) => {
									const Icon = MOBILE_BOTTOM_NAV_ICONS[item.tabName] ?? item.icon;
									const isActive = isPrimaryNavigationActive(item.name, currentPageName);
									const label = item.name.toLowerCase() === 'profile' ? 'My Profile' : item.tabName;

									return (
										<li key={item.name}>
											<SidebarItem
												href={item.href ?? '/'}
												icon={Icon}
												isActive={isActive}
												isExpanded={isExpanded}
												label={label}
											/>
										</li>
									);
								})}
							</SidebarList>
						</nav>
					</div>

					<nav aria-label="More" className="border-t-[0.5px] border-[#E4E8F3] pt-6">
						<SidebarList>
							<li
								aria-hidden="true"
								className={cn(
									'hidden shrink-0 items-center justify-center overflow-hidden text-[#6E7899] transition-[height,opacity] duration-200 [@media(max-height:900px)]:flex',
									hasReachedScrollEnd ? 'pointer-events-none h-0 opacity-0' : 'h-16 opacity-100',
									isExpanded ? 'w-[260px]' : 'w-14'
								)}
							>
								<span
									className={cn('flex h-6 items-center', isExpanded ? 'w-[186px] gap-4' : 'w-6')}
								>
									<MouseIcon className="size-6 shrink-0" />
									{isExpanded ? (
										<span className="whitespace-nowrap text-base font-medium leading-[22px] tracking-[-0.4px]">
											Scroll to see more
										</span>
									) : null}
								</span>
							</li>
							{secondaryNavigation.map((item) => {
								const itemName = item.name.toLowerCase();

								if (itemName === 'refer') {
									if (!item.href) return null;

									return (
										<li key={item.name}>
											<SidebarItem
												href={item.href}
												icon={ReferIcon}
												isActive={currentPageName.toLowerCase() === itemName}
												isExpanded={isExpanded}
												label="Refer & Get Rewards"
											/>
										</li>
									);
								}

								if (itemName === 'account') {
									if (!isRegisteredUser) return null;

									return (
										<li key={item.name}>
											<SidebarItem
												icon={SettingsIcon}
												isActive={currentPageName.toLowerCase() === itemName}
												isExpanded={isExpanded}
												label="Account Settings"
												onClick={onAccountClick}
											/>
										</li>
									);
								}

								if (itemName === 'howtoplay') {
									return (
										<li key={item.name}>
											<SidebarItem
												icon={HowToPlayIcon}
												isActive={currentPageName.toLowerCase() === itemName}
												isExpanded={isExpanded}
												label="How to play"
												onClick={onHowToPlayClick}
											/>
										</li>
									);
								}

								if (itemName === 'admin') {
									if (user?.role !== UserRole.Admin || !item.href) return null;

									return (
										<li key={item.name}>
											<SidebarItem
												href={item.href}
												icon={item.icon}
												isActive={currentPageName === 'Admin'}
												isExpanded={isExpanded}
												label="Admin"
											/>
										</li>
									);
								}

								return null;
							})}
						</SidebarList>
					</nav>

					<SidebarLegalNavigation
						className="mt-auto border-t-[0.5px] border-[#E4E8F3] pt-6"
						currentTimezone={currentTimezone}
						isExpanded={isExpanded}
					/>
				</div>

				<div
					className={cn(
						'mt-4 flex shrink-0 flex-col text-[#414D73] transition-[width] duration-300 ease-out',
						isExpanded ? 'w-[260px]' : 'w-14'
					)}
				>
					{!isLoadingUser && isRegisteredUser && (user || accessToken) ? (
						<div className="z-20 mt-auto mb-4 shrink-0 border-t-[0.5px] border-[#E4E8F3] bg-white pt-6">
							<SidebarItem
								className="text-[#2667FF] [&_.sidebar-item-label]:text-[#09090A]"
								icon={LogoutIcon}
								isExpanded={isExpanded}
								label="Logout"
								onClick={() => void signOut()}
							/>
						</div>
					) : null}
				</div>
			</div>
		</aside>
	);
}
